import { Injectable } from '@angular/core';
import { Router, NavigationStart, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { BehaviorSubject } from 'rxjs';

interface HistoryEntry {
  url: string;
  id: number;
}

@Injectable({
  providedIn: 'root'
})
export class UrlHistoryService {
  private history: HistoryEntry[] = [];
  private currentIndex = -1;
  private currentUrl = ''; // Track current URL to prevent loops

  private nonValidRoutes = [
    "/paystatus",
    "/livesplit/paymentfailed",
    "/cart/paymentfailed",
    "/glance",
    "/signin",
    "/signout",
  ];
  private browserBackSubject = new BehaviorSubject<number>(0);
  browserBack$ = this.browserBackSubject.asObservable();

  constructor(private router: Router, private auth: AuthService) {
    this.router.events.pipe(
      filter(event => event instanceof NavigationStart || event instanceof NavigationEnd)
    ).subscribe(event => {
      if (event instanceof NavigationStart) {
        if (event.navigationTrigger === 'popstate') {
          this.handlePopState(event);
        }
      } else if (event instanceof NavigationEnd) {
        this.handleNavigationEnd(event);
      }
    });
  }


  private handlePopState(event: NavigationStart) {
    this.browserBackSubject.next(Date.now());
    const restoredState = event.restoredState;
    if (restoredState) {
      const index = this.history.findIndex(entry => entry.id === restoredState.navigationId);
      if (index !== -1) {
        this.currentIndex = index;
      }
    }
  }

  private handleNavigationEnd(event: NavigationEnd) {
    const url = this.stripQueryParams(event.urlAfterRedirects);
    this.currentUrl = url; // Update current URL on navigation end
    const id = event.id;

    if (this.isNewImperativeNavigation()) {
      this.history = this.history.slice(0, this.currentIndex + 1);
      this.history.push({ url, id });
      this.currentIndex++;
    }
  }


  private isNewImperativeNavigation(): boolean {
    const navigation = this.router.getCurrentNavigation();
    return !!navigation && navigation.trigger === 'imperative';
  }

  private stripQueryParams(url: string): string {
    return url.split('?')[0];
  }

  navigateBack(fallbackPostFix: string = "/menu/food") {
    let targetIndex = this.findValidBackIndex();

    if (targetIndex >= 0) {
      const targetUrl = this.history[targetIndex].url;

      // Add loop prevention check
      if (targetUrl === this.currentUrl) {
        // If target is same as current URL, look further back
        this.currentIndex = targetIndex - 1;
        this.navigateBack(fallbackPostFix);
        return;
      }

      this.currentIndex = targetIndex;
      this.router.navigateByUrl(targetUrl);
    } else {
      this.fallbackNavigation(fallbackPostFix);
    }
  }

  private findValidBackIndex(): number {
    let targetIndex = this.currentIndex - 1;
    while (targetIndex >= 0) {
      const url = this.history[targetIndex].url;
      if (!this.isRestrictedRoute(url)) {
        return targetIndex;
      }
      targetIndex--;
    }
    return -1;
  }

  private isRestrictedRoute(url: string): boolean {
    return this.nonValidRoutes.some(route => url.includes(route));
  }


  // Modified fallbackNavigation with loop check
  private fallbackNavigation(postFix: string) {
    const fallbackUrl = `${this.auth.venueId}${postFix}`;

    // Prevent fallback loop
    if (this.currentUrl === fallbackUrl) {
      console.warn('Preventing fallback loop to same URL');
      return;
    }

    const currentNav = this.router.getCurrentNavigation();
    const navId = currentNav?.id || this.createFallbackNavId();

    this.router.navigateByUrl(fallbackUrl).then(() => {
      this.history = [{ url: fallbackUrl, id: navId }];
      this.currentIndex = 0;
    });
  }
  // Add this helper method
  private createFallbackNavId(): number {
    return Math.floor(Math.random() * 1000000) + 1;
  }


  clearHistory() {
    this.history = [];
    this.currentIndex = -1;
  }
}
